import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { firstValueFrom } from 'rxjs';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { LoginService } from '../../login/login.service';
import { ConfigurationsService } from '../../common/_services/configurations.service';
import { Configuration } from '../../common/_types/configuration';

@Component({
    selector: 'app-configurations-detail',
    templateUrl: './configurations-detail.component.html',
    styleUrls: ['./configurations-detail.component.scss'],
    standalone: false
})
export class ConfigurationsDetailComponent implements OnInit {

    public configurationFormGroup!: RxFormGroup;
    public configuration: Configuration = new Configuration();
    public allowWrite: boolean = true;
    public allowDelete: boolean = true;

    public dataTypeOptions = [
        { label: 'string', value: 'string' },
        { label: 'int', value: 'int' },
        { label: 'decimal', value: 'decimal' },
        { label: 'boolean', value: 'boolean' },
        { label: 'guid', value: 'guid' }
    ];

    public controlTypeOptions = [
        { label: 'input-text', value: 'input-text' },
        { label: 'input-number', value: 'input-number' },
        { label: 'checkbox', value: 'checkbox' },
        { label: 'dropdown', value: 'dropdown' }
    ];

    constructor(
        private configurationsService: ConfigurationsService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private topbarService: TopbarService,
        private formBuilder: RxFormBuilder,
        private formValidationService: FormValidatorService,
        private loginService: LoginService
    ) {
        this.configurationFormGroup = this.formBuilder.formGroup<Configuration>(this.configuration) as RxFormGroup;
    }

    async ngOnInit(): Promise<void> {
        this.topbarService.setTitle('Detalles de configuración');
        this.topbarService.addBreadcrumb('Configuraciones', '/configurations');
        this.allowWrite = this.loginService.hasPermission('COMPANY_CONFIGURATIONS.WRITE');
        this.allowDelete = this.loginService.hasPermission('COMPANY_CONFIGURATIONS.DELETE');

        const id = this.route.snapshot.params['id'];
        if (id != null && id !== '0') {
            await this.loadConfiguration(id);
        } else {
            const group = this.route.snapshot.queryParams['group'];
            if (group) {
                this.configurationFormGroup.patchValue({ group });
            }
        }
    }

    async loadConfiguration(id: string): Promise<void> {
        this.configuration = await firstValueFrom(this.configurationsService.get(id));
        this.configurationFormGroup.patchValue(this.configuration);
        if(this.configuration.system){
            this.configurationFormGroup.disable();
        }
    }

    async save(): Promise<void> {
        const valid = this.formValidationService.validateForm(this.configurationFormGroup);
        if (valid) {
            this.configuration = Object.assign(this.configuration, this.configurationFormGroup.value);
            await firstValueFrom(this.configurationsService.save(this.configuration));
            this.messageService.add({ closable: true, severity: 'success', summary: 'Configuración guardada correctamente.' });
            this.router.navigate(['configurations']);
        } else {
            this.messageService.add({ closable: true, severity: 'error', summary: 'Error', detail: 'Existen errores, por favor revise el formulario de datos.' });
        }
    }

    delete(): void {
        this.confirmationService.confirm({
            message: '¿Está seguro que desea eliminar la configuración?',
            header: 'Eliminar configuración',
            acceptLabel: 'Aceptar',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.configurationsService.delete(this.configuration.id).subscribe({
                    next: () => {
                        this.cancel();
                        this.messageService.add({ closable: true, severity: 'success', detail: 'Configuración eliminada correctamente.' });
                    },
                    error: (error: any) => {
                        this.messageService.add({ closable: true, severity: 'error', detail: error.error.title });
                    }
                });
            }
        });
    }

    cancel(): void {
        this.router.navigate(['configurations']);
    }
}
